import React from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import StarsIcon from "@mui/icons-material/Stars";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import backgroundImage1 from "../../../../../assets/images/overview-stats-bg1.svg";
import backgroundImage2 from "../../../../../assets/images/overview-stats-bg2.svg";
import backgroundImage3 from "../../../../../assets/images/overview-stats-bg3.svg";
import backgroundImage4 from "../../../../../assets/images/overview-stats-bg4.svg";
import CustomUnclickableOverviewCard from "../../../../../customComponents/CustomUnclickableOverviewCard";
import CustomOverviewRectangle from "../../../../../customComponents/CustomOverviewRectangle";
import { useGetStudentOverviewStatsQuery } from "../../../../../app/leaderboardApiSlice";

const formatCount = (n) => {
  if (n == null || isNaN(n)) return "0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (abs >= 1_000_000)     return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000)         return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const StudentOverview = () => {
  const navigate = useNavigate();

  const { data: stats, isLoading } = useGetStudentOverviewStatsQuery();

  return (
    <Box>
      {/* Top action cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr" },
          gap: { xs: 2.5, sm: 2, md: 2, lg: 2.5 },
          marginBottom: "2em",
        }}
      >
        {/* Leaderboard rank */}
        {isLoading ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        ) : (
          <CustomOverviewRectangle
            background={backgroundImage1}
            iconElement={<EmojiEventsIcon sx={{ fontSize: "2.8em", color: "#fff", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />}
            text="This Week's Rank"
            number={stats?.week_rank ? `#${stats.week_rank}` : "Unranked"}
            subtitle="View Leaderboard →"
            onClick={() => navigate("/lms/student/leaderboard")}
          />
        )}

        {/* Latest tournament rank */}
        {isLoading ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        ) : (
          <CustomOverviewRectangle
            background={backgroundImage2}
            iconElement={<SportsEsportsIcon sx={{ fontSize: "2.8em", color: "#fff", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />}
            text={stats?.latest_tournament?.name || "No Tournament Yet"}
            number={stats?.latest_tournament_rank ? `Ranked #${stats.latest_tournament_rank}` : stats?.latest_tournament ? "Unranked" : ""}
            subtitle={stats?.latest_tournament ? "View Tournament →" : "Join one to compete"}
            onClick={
              stats?.latest_tournament
                ? () => navigate(`/lms/student/quiz/tournament/${stats.latest_tournament.id}`)
                : () => navigate("/lms/student/quiz/tournament")
            }
          />
        )}
        {isLoading ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        ) : (
          <CustomOverviewRectangle
            background={backgroundImage3}
            iconElement={<MenuBookIcon sx={{ fontSize: "2.8em", color: "#fff", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />}
            text="Shared Notes"
            number={stats?.shared_notes_count != null ? formatCount(stats.shared_notes_count) : "0"}
            subtitle="View shared notes →"
            onClick={() => navigate("/lms/student/notes")}
          />
        )}

        {/* Total points */}
        {isLoading ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        ) : (
          <CustomOverviewRectangle
            background={backgroundImage4}
            iconElement={<StarsIcon sx={{ fontSize: "2.8em", color: "#fff", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />}
            text="Total Points (All Time)"
            number={stats?.total_points != null ? formatCount(stats.total_points) : "0"}
            subtitle={`${formatCount(stats?.total_questions ?? 0)} questions answered`}
          />
        )}

        {/* Tournaments */}
        {isLoading ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        ) : (
          <CustomOverviewRectangle
            background={backgroundImage3}
            iconElement={<PeopleAltIcon sx={{ fontSize: "2.8em", color: "#fff", filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }} />}
            text="Tournaments Joined"
            number={formatCount(stats?.tournaments_count ?? 0)}
            subtitle="Browse Tournaments →"
            onClick={() => navigate("/lms/student/quiz/tournament")}
          />
        )}
      </Box>

      {/* Bottom stat cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: { xs: 2.5, sm: 2, md: 2, lg: 2.5 },
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        ) : (
          <CustomUnclickableOverviewCard iconElement={<SchoolIcon sx={{ fontSize: "2.5em", color: "primary.safeOnLight" }} />} text="Class Quizzes Taken" number={formatCount(stats?.class_quizzes_taken ?? 0)} />
        )}
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        ) : (
          <CustomUnclickableOverviewCard iconElement={<MenuBookIcon sx={{ fontSize: "2.5em", color: "primary.safeOnLight" }} />} text="Practice Tests Taken" number={formatCount(stats?.practice_tests_taken ?? 0)} />
        )}
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
        ) : (
          <CustomUnclickableOverviewCard iconElement={<EmojiEventsIcon sx={{ fontSize: "2.5em", color: "primary.safeOnLight" }} />} text="Tournaments Played" number={formatCount(stats?.tournaments_count ?? 0)} />
        )}
      </Box>
    </Box>
  );
};

export default StudentOverview;
